import { useEffect, useRef, useState } from 'react'
import { loadStripe, type Stripe, type StripeEmbeddedCheckout } from '@stripe/stripe-js'
import { TESTPHASE_TAGE } from '@raporto/shared'
import { Marke } from './Marke.js'

/**
 * Die Zahlungsseite – Stripe im Haus, nicht der Kunde bei Stripe.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum eingebettet und nicht gehostet
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Die gehostete Kasse war ein Medienbruch: Ein Klick, und der Kunde stand auf
 * checkout.stripe.com. Fremde Adresszeile, fremdes Aussehen, kein Raporto
 * mehr – und daneben der Satz «Heute wird nichts belastet», den er auf der
 * vorherigen Seite gelesen hat und hier nicht mehr sieht. Genau an dieser
 * Stelle bricht ein Handwerker ab, der zum ersten Mal etwas im Netz bucht.
 *
 * Eingebettet bleibt alles an seinem Platz: unser Kopf, unsere Erklärung,
 * unsere Adresszeile. Die Zahlungsfelder rendert weiterhin Stripe, in einem
 * Rahmen, den Stripes eigenes JavaScript setzt.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was hier NICHT passiert
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Kein selbstgebautes Kartenformular. Kein von Hand gesetztes iframe. Diese
 * Seite stellt genau einen leeren Behälter hin und übergibt ihn Stripe; sie
 * sieht keine Kartennummer, keine Prüfziffer und kein Ablaufdatum.
 *
 * Und sie schaltet nichts frei. Der Betrieb entsteht nicht hier, sondern im
 * Webhook, nachdem Stripe die Zahlungsmethode bestätigt hat. Diese Seite
 * kennt nicht einmal eine Betriebskennung – es gibt noch keine.
 */

/**
 * Was vor dem Bezahlen steht – wörtlich.
 *
 * Vier Sätze, und der zweite ist der wichtigste: Er beantwortet die Frage,
 * die in diesem Moment jeder hat. Eine Zusage, die schlechter ist als die
 * Wirklichkeit, kostet Vertrauen – wer mit einer Abbuchung rechnet, sucht sie
 * auf dem Auszug und findet stattdessen den Grund, uns nicht zu glauben.
 *
 * Dieselben Worte wie in der Willkommensnachricht. Wer in der Bestellung
 * «Heute entstehen keine Kosten» gelesen hat und am nächsten Tag in der Mail
 * einen anders klingenden Satz findet, liest den Unterschied als Vorbehalt.
 *
 * Hier stand ein fünfter Satz, «Deine Zahlungsmethode wird sicher über Stripe
 * hinterlegt». Der Name gehört auf diese Seite – die Maske darunter ist
 * Stripes, und der Satz daneben sagt, warum Raporto die Kartendaten nicht
 * sieht. In der Zusage stand er ein zweites Mal, und zwar an der Stelle, an
 * der es um Geld und nicht um Technik geht.
 */
export const ZAHLUNGSZUSAGE = [
  `${TESTPHASE_TAGE} Tage kostenlos testen`,
  'Heute entstehen keine Kosten.',
  'Deine Zahlungsmethode wird sicher hinterlegt und erst nach Ende der Testphase verwendet.',
  'Kündigst du vorher, bleibt deine Testphase vollständig kostenlos.'
] as const

export function Zahlung({
  clientSecret,
  publicKey,
  sandbox,
  betrieb,
  aufZurueck,
  aufStart
}: {
  /** Der Schlüssel für GENAU DIESE Checkout-Sitzung. Kein Zustand, keine Berechtigung. */
  clientSecret: string | null
  publicKey: string
  sandbox: boolean
  betrieb: string
  aufZurueck: () => void
  aufStart: () => void
}): JSX.Element {
  const behaelter = useRef<HTMLDivElement | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    if (!clientSecret || !publicKey) {
      setLaedt(false)
      return
    }
    let abgeraeumt = false
    /*
      `kasse` steht ausserhalb, weil die Aufräumfunktion sie braucht – und
      `offen` daneben, weil TypeScript in der Aufräumfunktion sonst nur die
      Zuweisung von oben sieht und nicht die aus dem `await` darunter.
    */
    const offen: { kasse: StripeEmbeddedCheckout | null } = { kasse: null }

    const starten = async (): Promise<void> => {
      try {
        const stripe: Stripe | null = await loadStripe(publicKey)
        if (abgeraeumt) return
        if (!stripe) throw new Error('Stripe konnte nicht geladen werden.')
        /*
          `createEmbeddedCheckoutPage` – so heisst der Aufruf seit Version 9
          von `@stripe/stripe-js`; ältere Anleitungen im Netz nennen
          `initEmbeddedCheckout`. Gemessen an den Typdefinitionen des
          installierten Pakets, nicht aus dem Gedächtnis.
        */
        const kasse = await stripe.createEmbeddedCheckoutPage({ clientSecret })
        offen.kasse = kasse
        if (abgeraeumt) {
          kasse.destroy()
          return
        }
        if (behaelter.current) kasse.mount(behaelter.current)
        setLaedt(false)
      } catch (e) {
        if (abgeraeumt) return
        setLaedt(false)
        /*
          Der Fehlertext von Stripe geht NICHT unverändert hinaus.

          Er nennt im Zweifel Konto- und Sitzungskennungen, und eine
          Fehlermeldung ist ein Protokoll. Was der Kunde braucht, ist die
          Auskunft, dass nichts belastet wurde und wie es weitergeht.
        */
        setFehler(
          'Der Zahlungsbereich konnte nicht geladen werden. Kosten sind keine entstanden. ' +
            'Lade die Seite neu oder versuche es in einem Moment noch einmal.'
        )
      }
    }
    void starten()

    /*
      Aufräumen ist bei dieser Einbettung Pflicht, nicht Höflichkeit.

      Stripe erlaubt nur EINE eingebettete Kasse je Seite. Bleibt beim
      Verlassen eine stehen, wirft der nächste Versuch – und der nächste
      Versuch ist der zweite Anlauf desselben Kunden.
    */
    return () => {
      abgeraeumt = true
      try {
        offen.kasse?.destroy()
      } catch {
        /* Schon zerlegt: dann ist das Ziel ohnehin erreicht. */
      }
    }
  }, [clientSecret, publicKey])

  return (
    <div className="rp-public rp-bestellung rp-zahlseite">
      <header className="rp-best-kopf">
        <button type="button" className="rp-marke" onClick={aufStart} id="btn-marke">
          <Marke groesse={26} />
        </button>
        <button type="button" className="rp-textknopf" id="btn-zahl-zurueck" onClick={aufZurueck}>
          Zurück zur Bestellung
        </button>
      </header>

      <main className="rp-best-blatt rp-zahl-blatt">
        <section className="rp-best-teil rp-zahl-erklaerung" id="zahlung-erklaerung">
          <h1>Zahlungsmethode hinterlegen</h1>
          <p className="rp-best-unter">
            {betrieb ? `Für ${betrieb}. ` : ''}Die Eingabe läuft über unseren
            Zahlungsdienstleister Stripe – deine Kartendaten sieht Raporto nicht.
          </p>

          <div className="rp-best-zusage" id="zahl-zusage">
            <ul>
              {ZAHLUNGSZUSAGE.map((satz) => (
                <li key={satz}>{satz}</li>
              ))}
            </ul>
            {sandbox && (
              <p className="rp-best-sandbox" id="zahl-sandbox">
                Testmodus – es fliesst kein echtes Geld.
              </p>
            )}
          </div>
        </section>

        {/*
          Sobald Stripe steht, fällt UNSER Rahmen weg.

          Vorher lag Stripes weisse Maske in einer dunklen Karte mit eigenem
          Rand, eigener Rundung und 460 px Mindesthöhe. Zwei Rahmen um
          dieselbe Sache, und ringsherum dunkle Fläche, in der die Maske wie
          ein Fremdkörper schwebte. Die Maske bringt ihre eigene Fläche mit –
          also bekommt sie die Seite und keinen Kasten darum.

          Solange sie NICHT steht – beim Laden, bei einem Fehler, bei einem
          abgelaufenen Vorgang –, bleibt die Karte: Dieser Text braucht einen
          Grund, auf dem er steht.
        */}
        <section
          className={`rp-best-teil rp-zahl-kasse${
            clientSecret && !fehler && !laedt ? ' rp-zahl-kasse-offen' : ''
          }`}
          id="zahlung-kasse"
        >
          {fehler && (
            <div className="rp-best-fehler" id="zahlung-fehler">
              {fehler}
            </div>
          )}

          {!clientSecret && !fehler && (
            <div className="rp-zahl-hinweis" id="zahlung-abgelaufen">
              <h2>Dieser Schritt ist nicht mehr offen</h2>
              <p>
                Diese Seite lässt sich nicht neu laden – sie gilt für einen Versuch. Kosten sind
                keine entstanden. Geh zurück zur Bestellung, deine Angaben stehen noch da.
              </p>
              <button
                type="button"
                className="rp-knopf rp-knopf-gold rp-knopf-gross"
                id="btn-zahl-neu"
                onClick={aufZurueck}
              >
                Zurück zur Bestellung
              </button>
            </div>
          )}

          {laedt && clientSecret && (
            <div className="rp-zahl-warte" id="zahlung-laedt">
              <div className="rp-einr-kreisel" aria-hidden="true" />
              <p>Zahlungsbereich wird geladen …</p>
            </div>
          )}

          {/* Der Behälter. Was darin erscheint, setzt Stripe. */}
          <div ref={behaelter} id="stripe-kasse" />
        </section>
      </main>
    </div>
  )
}
